import { SITE_URL, SITE_NAME } from "@/lib/site";
import { CONTACT, LEGAL } from "@/lib/policyMeta";

/** Registered office — Mumbai city center for geo signals (no street published). */
export const OFFICE_GEO = {
  latitude: 19.076,
  longitude: 72.8777,
  region: "IN-MH",
  placename: "Mumbai, Maharashtra, India",
  radiusKm: 150,
};

/** Cities & towns within ~150 km of Mumbai office — local SEO coverage (head/schema only). */
export const SERVICE_AREA_CITIES = [
  "Mumbai",
  "South Mumbai",
  "Navi Mumbai",
  "Thane",
  "Kalyan",
  "Dombivli",
  "Ulhasnagar",
  "Ambernath",
  "Badlapur",
  "Bhiwandi",
  "Mira Bhayandar",
  "Bhayandar",
  "Vasai",
  "Virar",
  "Nallasopara",
  "Naigaon",
  "Palghar",
  "Boisar",
  "Dahanu",
  "Vashi",
  "Nerul",
  "Kharghar",
  "Panvel",
  "Belapur",
  "Airoli",
  "Turbhe",
  "Kopar Khairane",
  "Kamothe",
  "Taloja",
  "Karanjade",
  "Andheri",
  "Bandra",
  "Borivali",
  "Chembur",
  "Colaba",
  "Dadar",
  "Goregaon",
  "Juhu",
  "Kandivali",
  "Malad",
  "Mulund",
  "Powai",
  "Santacruz",
  "Versova",
  "Worli",
  "Karjat",
  "Khopoli",
  "Lonavala",
  "Khandala",
  "Talegaon",
  "Neral",
  "Matheran",
  "Pen",
  "Alibaug",
  "Murud",
  "Roha",
  "Mahad",
  "Pune",
  "Pimpri",
  "Chinchwad",
  "Hinjewadi",
  "Wagholi",
  "Chakan",
  "Rajgurunagar",
  "Dehu Road",
  "Igatpuri",
  "Shahapur",
  "Asangaon",
  "Kasara",
  "Jawhar",
  "Wada",
  "Bhiwandi Nizampur",
  "Kalwa",
  "Mumbra",
  "Diva",
  "Titwala",
  "Shahad",
  "Kalyan West",
  "Kalyan East",
];

/** Branded + commercial keywords for meta (head only — no visible copy). */
export const BRAND_KEYWORDS = [
  "OORI",
  "OORI Label",
  "Oori Label",
  "Oori",
  "oorilabel",
  "oori label",
  "OORI label",
  "OORI Label official",
  "OORI Label official website",
  "OORI Label official store",
  "Oori Label official store",
  "buy OORI Label",
  "buy Oori Label",
  "OORI Label online",
  "Oori Label online",
  "OORI Label India",
  "Oori Label India",
  "OORI Label Mumbai",
  "Oori Label Mumbai",
  "OORI Label shop",
  "OORI Label kurtis",
  "OORI Label dresses",
  "OORI Label co-ords",
  "OORI Label tops",
  "OORI Label ethnic wear",
  "OORI Label women's fashion",
  "OORI Label contemporary Indian fashion",
  "OORI means home",
  "OORI fashion",
  "OORI clothing",
  "OORI brand",
  "women's kurtis Mumbai",
  "women's co-ords Maharashtra",
  "contemporary Indian fashion India",
];

/** Alternate brand spellings for schema / AI discovery. */
export const BRAND_ALTERNATE_NAMES = [
  "OORI Label",
  "OORI",
  "Oori Label",
  "Oori",
  "oorilabel",
  "oori label",
  "OORI label",
];

export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-share.png`;

export const LOCAL_AREA_PHRASE = `Mumbai, Navi Mumbai, Thane, Pune & ${SERVICE_AREA_CITIES.length}+ cities within ${OFFICE_GEO.radiusKm} km`;

export const SEO_DEFAULTS = {
  title: `OORI Label | Official Store — Contemporary Indian Women's Fashion`,
  description: `OORI Label (also searched as OORI) — official online store for contemporary Indian kurtis, co-ords, dresses & tops. OORI means home. Designed in Mumbai. Delivery across ${LOCAL_AREA_PHRASE}.`,
  keywords: BRAND_KEYWORDS.join(", "),
};

export const NOINDEX_PATH_PREFIXES = [
  "/admin",
  "/cart",
  "/checkout",
  "/login",
  "/register",
  "/account",
  "/wishlist",
];

export function isNoindexPath(pathname) {
  if (!pathname) return false;
  if (pathname.startsWith("/order/")) return true;
  if (pathname.startsWith("/search")) return true;
  return NOINDEX_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function absoluteUrl(path = "/") {
  if (!path) return SITE_URL;
  if (path.startsWith("http")) return path;
  return `${SITE_URL}${path.startsWith("/") ? path : `/${path}`}`;
}

export function truncateMeta(text, max = 160) {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trim()}…`;
}

export function stripHtml(text) {
  return String(text || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

export const STATIC_ROUTE_SEO = {
  "/": {
    title: SEO_DEFAULTS.title,
    description: SEO_DEFAULTS.description,
    keywords: SEO_DEFAULTS.keywords,
  },
  "/about": {
    title: `About OORI Label | Founder Story — Contemporary Indian Fashion Mumbai`,
    description: `About OORI Label (OORI) — Mumbai-based contemporary Indian fashion for women. Founded by Payal. Kurtis, co-ords & tops crafted with intention. Serving ${LOCAL_AREA_PHRASE}.`,
    keywords: [...BRAND_KEYWORDS.slice(0, 12), "Payal OORI Label", "OORI Label founder"].join(", "),
  },
  "/policies": {
    title: `Policies | OORI Label — Shipping, Returns & Privacy`,
    description: `OORI Label policies — shipping across Maharashtra & India, exchanges, returns, privacy & contact. Registered office: ${LEGAL.registeredOffice}.`,
  },
  "/policies/terms": {
    title: `Terms of Service | OORI Label`,
    description: `Terms of Service for shopping at OORI Label (${LEGAL.entity}). Governed by laws of India; jurisdiction: ${LEGAL.jurisdictionCity}.`,
  },
  "/policies/privacy": {
    title: `Privacy Policy | OORI Label`,
    description: `How OORI Label collects, uses & protects your data when you shop at oorilabel.com. Contact: ${CONTACT.email}.`,
  },
  "/policies/shipping": {
    title: `Shipping Policy | OORI Label — Mumbai & Pan-India Delivery`,
    description: `OORI Label shipping — flat ₹99 delivery across ${LOCAL_AREA_PHRASE} and all India.`,
  },
  "/policies/returns": {
    title: `Exchange & Returns | OORI Label`,
    description: `OORI Label exchange & returns policy. Hassle-free support via ${CONTACT.email} or WhatsApp.`,
  },
  "/policies/contact": {
    title: `Help & Contact | OORI Label`,
    description: `Contact OORI Label — ${CONTACT.email}, WhatsApp ${CONTACT.whatsapp}. Official OORI / OORI Label customer support from Mumbai.`,
  },
};

export function policySeoForSlug(slug) {
  const key = slug ? `/policies/${slug}` : "/policies";
  return STATIC_ROUTE_SEO[key] || STATIC_ROUTE_SEO["/policies"];
}

export function categorySeo(category, productCount) {
  const name = category?.name || "Women's Fashion";
  const slug = category?.slug || "";
  const countHint = productCount ? ` ${productCount}+ styles.` : "";
  return {
    title: `${name} | OORI Label — Women's ${name}`,
    description: truncateMeta(
      `Shop ${name.toLowerCase()} at OORI Label (OORI) — contemporary Indian women's ${name.toLowerCase()} online.${countHint} Delivery across ${LOCAL_AREA_PHRASE}.`
    ),
    canonical: absoluteUrl(`/category/${slug}`),
    keywords: [
      ...BRAND_KEYWORDS.slice(0, 10),
      `OORI Label ${name}`,
      `OORI ${name}`,
      `${name} Mumbai`,
      `women's ${name.toLowerCase()} Maharashtra`,
    ].join(", "),
  };
}

/** Prefer the design's primary product URL so color variants don't compete as duplicates. */
export function primaryProductId(product, colorVariants = []) {
  if (!product?.id) return null;
  const fromVariants = (colorVariants || []).find((v) => v?.is_primary)?.id;
  if (fromVariants) return fromVariants;
  if (product.is_primary !== false) return product.id;
  const match = String(product.id).match(/^(.+)-[a-z]+$/i);
  return match ? match[1] : product.id;
}

export function productSeo(product, colorVariants = []) {
  if (!product) return null;
  const name = product.name || "Product";
  const categoryName = product.category_name || product.category || "Fashion";
  const price = product.price ? ` ₹${product.price}` : "";
  const snippet = truncateMeta(stripHtml(product.description), 90);
  const canonicalId = primaryProductId(product, colorVariants) || product.id;
  const isVariant = product.is_primary === false && canonicalId !== product.id;
  return {
    title: `${name} | ${categoryName} | OORI Label`,
    description: truncateMeta(
      `Buy ${name} at OORI Label (OORI).${price ? ` Price${price}.` : ""} ${categoryName} for women — delivered across ${LOCAL_AREA_PHRASE}.${snippet ? ` ${snippet}` : ""}`
    ),
    canonical: absoluteUrl(`/product/${canonicalId}`),
    noindex: isVariant,
    keywords: [
      ...BRAND_KEYWORDS.slice(0, 8),
      `OORI Label ${name}`,
      `OORI ${name}`,
      `${name} ${categoryName}`,
      `buy ${name} Mumbai`,
    ].join(", "),
  };
}

export function searchSeo(query) {
  const q = (query || "").trim();
  return {
    title: q ? `Search: ${q} | OORI Label` : `Search | OORI Label`,
    description: q
      ? `Search results for "${q}" at OORI Label — contemporary Indian women's fashion in Mumbai & Maharashtra.`
      : `Search OORI Label for kurtis, co-ords, tops & more.`,
    canonical: absoluteUrl(q ? `/search?q=${encodeURIComponent(q)}` : "/search"),
    noindex: true,
  };
}
